import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, MessageCircle } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { CommentsSection } from "@/components/rawan/CommentsSection";
import { useTheme } from "@/lib/theme-context";

type Search = { focus?: string; t?: string };

export const Route = createFileRoute("/c/$threadId")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    focus: typeof raw.focus === "string" ? raw.focus : undefined,
    t: typeof raw.t === "string" ? raw.t : undefined,
  }),
  head: () => ({
    meta: [
      { title: "تعليق — Rawan UI" },
      { name: "description", content: "صفحة خفيفة مخصصة لعرض التعليقات من الإشعارات." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CommentThreadPage,
});

function decodePart(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function CommentThreadPage() {
  const { threadId: rawThreadId } = Route.useParams();
  const { t: titleParam } = Route.useSearch();
  const { dir, language } = useTheme();
  const isAr = dir === "rtl" || language === "ar";
  const tr = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);

  const threadId = decodePart(rawThreadId);
  const match = /^(movie|series|story)[-:](.+)$/.exec(threadId);
  const kind = match?.[1] ?? null;
  const slug = match?.[2] ?? null;
  const slugLabel = slug ? slug.replace(/[-_]+/g, " ") : threadId;
  const title = (titleParam && titleParam.trim()) || slugLabel;
  const fullHref = kind && slug ? `/${kind}/${slug}#comments` : null;

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto max-w-3xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-border bg-card/60 px-4 py-3 shadow-sm">
          <div className="flex items-start gap-3 min-w-0">
            <div className="grid place-items-center w-9 h-9 rounded-xl bg-primary/10 text-primary shrink-0">
              <MessageCircle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
                {tr("صفحة التعليق", "Comment thread")}
              </div>
              <div className="truncate text-sm font-bold mt-0.5">{title}</div>
            </div>
          </div>
          {fullHref && (
            <a
              href={fullHref}
              className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline shrink-0 whitespace-nowrap"
            >
              {tr("النقاش الكامل", "Full discussion")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        <CommentsSection threadId={threadId} title={title} />
      </main>
      <Footer />
    </div>
  );
}
